import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Plus, Copy, Trash2, X, Dices } from "lucide-react";
import { sectionsForFormat, type DocFormat } from "@/content/designDocs";
import { useDesignDocStore } from "@/store/designDocStore";
import { useDocFormats, useDocSectionMap } from "@/store/designDocContentStore";
import { useJamStore } from "@/store/jamStore";
import { prefillFromConcept } from "@/core/docPrefill";
import DocEditor from "@/features/docs/DocEditor";

export default function Docs() {
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState<DocFormat | null>(null);

  // Opened directly from elsewhere (e.g. Jam's "Create Pitch") with a doc id.
  const location = useLocation();
  useEffect(() => {
    const openId = (location.state as { openDocId?: string } | null)?.openDocId;
    if (openId) setSelected(openId);
    // Run once on mount — the navigation state is only meaningful on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const documents = useDesignDocStore((s) => s.documents);
  const create = useDesignDocStore((s) => s.create);
  const remove = useDesignDocStore((s) => s.remove);
  const duplicate = useDesignDocStore((s) => s.duplicate);
  const formats = useDocFormats();
  const sectionMap = useDocSectionMap();

  if (selected) {
    return (
      <DocEditor docId={selected} onBack={() => setSelected(null)} />
    );
  }

  function progressFor(formatId: string, values: Record<string, string>): { filled: number; total: number } {
    const fmt = formats.find((f) => f.id === formatId);
    if (!fmt) return { filled: 0, total: 0 };
    const secs = sectionsForFormat(fmt, sectionMap).filter((s) => s.id !== "title");
    const filled = secs.filter((s) => (values[s.id] ?? "").trim()).length;
    return { filled, total: secs.length };
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-surface-2 text-primary">
          <FileText className="h-6 w-6" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-text">Design Docs</h2>
          <p className="text-sm text-muted">Turn a game idea into a real document — from a one-page hook up to a complete GDD.</p>
        </div>
      </div>

      {/* Start a new document */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Start a new document</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {formats.map((f) => (
            <button
              key={f.id}
              onClick={() => setCreating(f)}
              className="group flex flex-col rounded-2xl border border-border bg-surface p-4 text-left transition-colors hover:border-primary/50"
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-semibold text-text">{f.name}</span>
                <span className="text-[11px] text-muted">{f.sectionIds.filter((id) => id !== "title").length} sections</span>
              </div>
              <p className="mt-1 text-sm text-muted">{f.blurb}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                <Plus className="h-3.5 w-3.5" /> New {f.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Existing documents */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Your documents</h3>
        {documents.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-surface/50 px-4 py-8 text-center text-sm text-muted">
            No documents yet. Pick a format above to begin.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {documents.map((d) => {
              const fmt = formats.find((f) => f.id === d.formatId);
              const { filled, total } = progressFor(d.formatId, d.values);
              const pct = total ? Math.round((filled / total) * 100) : 0;
              return (
                <div key={d.id} className="rounded-2xl border border-border bg-surface p-4">
                  <button onClick={() => setSelected(d.id)} className="block w-full text-left">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-display font-semibold text-text">{d.title}</span>
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{fmt?.name ?? "Unknown"}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1 text-[11px] text-muted">{filled}/{total} sections{(d.links?.length ?? 0) > 0 ? ` · ${d.links!.length} linked` : ""} · updated {new Date(d.updatedAt).toLocaleDateString()}</div>
                  </button>
                  <div className="mt-3 flex items-center gap-1">
                    <button onClick={() => setSelected(d.id)} className="rounded-lg bg-surface-2 px-2.5 py-1 text-xs font-medium text-text hover:bg-border/40">Open</button>
                    <button onClick={() => { const id = duplicate(d.id); if (id) setSelected(id); }} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted hover:text-text">
                      <Copy className="h-3.5 w-3.5" /> Duplicate
                    </button>
                    <button onClick={() => { if (confirm(`Delete "${d.title}"? This can't be undone.`)) remove(d.id); }} className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted hover:text-amber-400">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {creating && (
        <CreateModal
          format={creating}
          onClose={() => setCreating(null)}
          onCreate={(title, values) => {
            const id = create(creating.id, { title, values });
            setCreating(null);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function CreateModal({
  format, onClose, onCreate,
}: {
  format: DocFormat;
  onClose: () => void;
  onCreate: (title: string, values: Record<string, string>) => void;
}) {
  const favorites = useJamStore((s) => s.favorites);
  const [prefill, setPrefill] = useState(false);
  const [favId, setFavId] = useState<string>(favorites[0]?.id ?? "");
  const [title, setTitle] = useState("Untitled game");

  const chosen = useMemo(() => favorites.find((f) => f.id === favId), [favorites, favId]);
  const prefillData = useMemo(() => (chosen ? prefillFromConcept(chosen.concept) : null), [chosen]);

  const effectiveTitle = prefill && prefillData ? prefillData.title : title;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} className="relative w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-text">New {format.name}</h3>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted hover:bg-surface-2 hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-4 text-sm text-muted">{format.blurb}</p>

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">Game title</label>
        <input
          value={prefill && prefillData ? prefillData.title : title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={prefill && !!prefillData}
          className="mb-4 w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          placeholder="Untitled game"
        />

        <label className={`flex items-center gap-2 rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-sm ${favorites.length ? "cursor-pointer" : "opacity-50"}`}>
          <input type="checkbox" checked={prefill} disabled={!favorites.length} onChange={(e) => setPrefill(e.target.checked)} className="h-4 w-4 accent-primary" />
          <Dices className="h-4 w-4 text-primary" />
          <span className="text-text">Prefill from a saved Jam concept</span>
        </label>

        {prefill && favorites.length > 0 && (
          <select value={favId} onChange={(e) => setFavId(e.target.value)} className="mt-2 w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:border-primary focus:outline-none">
            {favorites.map((f) => (
              <option key={f.id} value={f.id}>{f.concept.title}</option>
            ))}
          </select>
        )}
        {!favorites.length && (
          <p className="mt-2 text-xs text-muted">Save a concept in Game-Jam Ideas to enable prefilling.</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted hover:text-text">Cancel</button>
          <button
            onClick={() => onCreate(effectiveTitle, prefill && prefillData ? prefillData.values : {})}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        </div>
      </motion.div>
    </div>
  );
}
